import { create } from "zustand";
import { analyzeDocument, type DocumentMetrics } from "../services/analysis";
import { api } from "../lib/api";

export interface MetricSnapshot {
  id: string;
  timestamp: string;
  snapshotName?: string;
  wordCount: number;
  readabilityScore: number;
  citationCount: number;
  submissionReadiness: number;
}

interface VitalsState {
  metrics: DocumentMetrics | null;
  history: MetricSnapshot[];
  isAnalyzing: boolean;
  isLoadingHistory: boolean;

  analyze: (content: string, docType: "latex" | "markdown" | "docx") => void;
  fetchHistory: (documentId: string) => Promise<void>;
  recordSnapshot: (content: string, docType: "latex" | "markdown" | "docx") => void;
}

let analysisTimer: ReturnType<typeof setTimeout> | null = null;
let wordCountHistory: { timestamp: number; count: number }[] = [];

function computeVelocity(currentWordCount: number): number {
  const now = Date.now();
  wordCountHistory.push({ timestamp: now, count: currentWordCount });

  const cutoff = now - 60000;
  wordCountHistory = wordCountHistory.filter((e) => e.timestamp >= cutoff);

  if (wordCountHistory.length < 2) return 0;

  const first = wordCountHistory[0];
  const last = wordCountHistory[wordCountHistory.length - 1];
  const minutesElapsed = (last.timestamp - first.timestamp) / 60000;

  if (minutesElapsed < 0.1) return 0;
  return Math.max(0, Math.round((last.count - first.count) / minutesElapsed));
}

export const useVitalsStore = create<VitalsState>()((set) => ({
  metrics: null,
  history: [],
  isAnalyzing: false,
  isLoadingHistory: false,

  analyze: (content, docType) => {
    if (analysisTimer) clearTimeout(analysisTimer);

    analysisTimer = setTimeout(() => {
      set({ isAnalyzing: true });

      try {
        const velocity = computeVelocity(
          content.split(/\s+/).filter((w) => w.length > 0).length,
        );

        const metrics = analyzeDocument(content, docType, velocity);
        set({ metrics, isAnalyzing: false });
      } catch {
        set({ isAnalyzing: false });
      }
    }, 500);
  },

  fetchHistory: async (documentId) => {
    set({ isLoadingHistory: true });

    try {
      const resp = await api.get<{ snapshots: MetricSnapshot[] }>(
        `/api/v1/vitals/history/${documentId}`,
      );
      set({ history: resp.snapshots, isLoadingHistory: false });
    } catch {
      set({ isLoadingHistory: false });
    }
  },

  recordSnapshot: (content, docType) => {
    const metrics = analyzeDocument(content, docType);
    const snapshot: MetricSnapshot = {
      id: `local-${Date.now()}`,
      timestamp: new Date().toISOString(),
      wordCount: metrics.wordCount,
      readabilityScore: metrics.readabilityScore,
      citationCount: metrics.citationCount,
      submissionReadiness: metrics.submissionReadiness,
    };

    set((s) => ({
      history: [...s.history, snapshot].slice(-50),
    }));
  },
}));
